import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

import { FormAttachmentViewer } from 'src/Components/Forms/FormAttachmentViewer';
import { UploadFileButton } from 'src/Components/Inputs/UploadFileButton';
import { useFetchStorageItemInformation } from 'src/Hooks/Mutations/useFetchStorageItemInformation';
import { useFileUploadManager } from 'src/Hooks/Shared/useFileUploadManager';
import { AssetStorageItemDtoTypeEnum } from 'src/Services/API/generated';

import styles from './InventoryInvoices.module.scss';
import { Props } from './InventoryInvoices.types';

/** Folder of Documents where the invoices added from the inventory are filed. */
const INVOICES_FOLDER = '/Factures de l’inventaire/';

/**
 * The object's invoices, with the same button and list as the attachments of the
 * instructions: photograph a paper invoice, import a file, or link a document already in
 * Documents. An invoice always lives in Documents; removing it here only detaches it.
 */
const InventoryInvoices = ({ invoices, onAdd, onRemove }: Props) => {
  const { t } = useTranslation();
  const { mutateAsync: fetchFileInfo } = useFetchStorageItemInformation();
  const { uploads, startUpload, removeUpload, isLoading } = useFileUploadManager({
    onUploadReady: ready => {
      if (!ready.fileData) return;
      onAdd({
        id: ready.fileData.id,
        name: ready.fileData.name,
        previewUrl: ready.fileData.urls?.previewThumbnailUrl,
      });
      removeUpload(ready.localId);
    },
  });

  const linkExisting = async (fileIds: string[]) => {
    try {
      const files = await Promise.all(fileIds.map(fileId => fetchFileInfo(fileId)));
      files
        .filter(file => !invoices.some(invoice => invoice.id === file.id))
        .forEach(file =>
          onAdd({ id: file.id, name: file.name, previewUrl: file.urls?.previewThumbnailUrl })
        );
    } catch {
      notifications.show({
        color: 'red',
        message: t(
          'inventory.invoices.linkError',
          "La facture n'a pas pu être ajoutée. Réessayez."
        ),
      });
    }
  };

  return (
    <div className={styles.container}>
      <UploadFileButton
        onFilesSelected={files => startUpload(files, INVOICES_FOLDER)}
        onExistingFilesSelect={linkExisting}
        loading={isLoading}
        label={t('inventory.invoices.add', 'Ajouter une facture')}
        modalTitle={t('inventory.invoices.modalTitle', 'Ajouter une facture')}
        fileUploadLabel={t('inventory.invoices.uploadLabel', 'Importer une facture')}
        existingFileLabel={t('inventory.invoices.existingLabel', 'Lier une facture de Documents')}
      />
      <FormAttachmentViewer
        attachments={invoices.map(invoice => ({
          fileId: invoice.id,
          name: invoice.name,
          previewUrl: invoice.previewUrl,
          type: AssetStorageItemDtoTypeEnum.File,
        }))}
        uploadingAttachments={uploads}
        onDeleteAttachment={attachment => attachment.fileId && onRemove(attachment.fileId)}
        onDeleteUploadingAttachment={localId => removeUpload(localId)}
      />
    </div>
  );
};

export default InventoryInvoices;
